import { formatMoney } from '../../utils/currency';

// Utilidades puras del plano del salón. Posición y tamaño se manejan en celdas de grilla (igual que el backend),
// la pantalla las multiplica por CELL para dibujar.

export const CELL = 40;
export const MAX_GRID = 200;
export const MAX_SIZE = 40;
export const MIN_CANVAS_COLS = 24;
export const MIN_CANVAS_ROWS = 14;
export const CANVAS_MARGIN = 4;

export const SHAPES = [
  { value: 'SQUARE', label: 'Cuadrada', width: 2, height: 2 },
  { value: 'RECTANGLE', label: 'Rectangular', width: 3, height: 2 },
  { value: 'ROUND', label: 'Redonda', width: 2, height: 2 },
];

export const shapeDefaults = (shape) => SHAPES.find((item) => item.value === shape) || SHAPES[0];

export const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const rectOf = (table) => ({ x: table.posX, y: table.posY, w: table.width, h: table.height });

export const overlaps = (a, b) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

export const hasCollision = (rect, tables, ignoreId) =>
  tables.some((table) => table.id !== ignoreId && overlaps(rect, rectOf(table)));

/** Mantiene el rectángulo dentro de la grilla y con tamaño válido. */
export const clampRect = ({ x, y, w, h }) => {
  const width = clamp(Math.round(w), 1, MAX_SIZE);
  const height = clamp(Math.round(h), 1, MAX_SIZE);
  return {
    x: clamp(Math.round(x), 0, MAX_GRID - width),
    y: clamp(Math.round(y), 0, MAX_GRID - height),
    w: width,
    h: height,
  };
};

/** Primer lugar libre (recorriendo filas) para una mesa de w×h sin pisar a las existentes. */
export const findFreeSpot = (tables, w, h, cols = MIN_CANVAS_COLS) => {
  for (let y = 0; y <= MAX_GRID - h; y += 1) {
    for (let x = 0; x <= Math.max(cols - w, 0); x += 1) {
      if (!hasCollision({ x, y, w, h }, tables)) return { x, y };
    }
  }
  return { x: 0, y: 0 };
};

/** "M1", "M2"… el primero que no esté en uso (sin distinguir mayúsculas). */
export const nextTableName = (tables) => {
  const taken = new Set(tables.map((table) => table.name.trim().toLowerCase()));
  let n = 1;
  while (taken.has(`m${n}`)) n += 1;
  return `M${n}`;
};

export const canvasSize = (tables) => {
  const right = tables.reduce((max, table) => Math.max(max, table.posX + table.width), 0);
  const bottom = tables.reduce((max, table) => Math.max(max, table.posY + table.height), 0);
  return {
    cols: Math.min(Math.max(MIN_CANVAS_COLS, right + CANVAS_MARGIN), MAX_GRID),
    rows: Math.min(Math.max(MIN_CANVAS_ROWS, bottom + CANVAS_MARGIN), MAX_GRID),
  };
};

/** Tamaño del plano en servicio: ajustado a las mesas (más un borde), sin la grilla mínima del editor. */
export const contentSize = (tables) => {
  const right = tables.reduce((max, table) => Math.max(max, table.posX + table.width), 0);
  const bottom = tables.reduce((max, table) => Math.max(max, table.posY + table.height), 0);
  return { cols: Math.min(Math.max(right + 1, 6), MAX_GRID), rows: Math.min(Math.max(bottom + 1, 4), MAX_GRID) };
};

/** Escala para que el plano entre en el ancho disponible, sin bajar de una escala legible (después se desplaza). */
export const fitScale = (availableWidth, cols, minScale = 0.55) => {
  const fit = availableWidth / (cols * CELL);
  if (!Number.isFinite(fit) || fit <= 0) return 1;
  return Math.max(minScale, Math.min(1, fit));
};

const GEOMETRY_KEYS = ['posX', 'posY', 'width', 'height', 'rotation'];

export const geometryChanged = (original, current) => GEOMETRY_KEYS.some((key) => original[key] !== current[key]);

export const metaChanged = (original, current) =>
  String(original.name).trim() !== String(current.name).trim() ||
  Number(original.capacity) !== Number(current.capacity) ||
  original.shape !== current.shape;

/** Mesas con nombre, capacidad o forma modificados (para PUT /tables/dining/{id}). */
export const buildMetaChanges = (originalTables, draftTables) => {
  const originalById = new Map(originalTables.map((table) => [table.id, table]));
  return draftTables
    .filter((table) => {
      const original = originalById.get(table.id);
      return original && metaChanged(original, table);
    })
    .map((table) => ({ id: table.id, name: String(table.name).trim(), capacity: Number(table.capacity), shape: table.shape }));
};

/** Primer problema de validación de los datos editados, o null si todo está bien. */
export const validateMeta = (changes) => {
  for (const change of changes) {
    if (!change.name) return 'El nombre de la mesa no puede estar vacío.';
    if (change.name.length > 50) return `El nombre "${change.name}" es muy largo (máximo 50).`;
    if (!Number.isInteger(change.capacity) || change.capacity < 1 || change.capacity > 50) {
      return `La capacidad de la mesa ${change.name} debe estar entre 1 y 50.`;
    }
  }
  const names = changes.map((change) => change.name.toLowerCase());
  if (new Set(names).size !== names.length) return 'Hay dos mesas con el mismo nombre.';
  return null;
};

/**
 * Al pasar una mesa cuadrada (ancho = alto) a rectangular se le agrega una celda de ancho para que el cambio se vea,
 * si hay lugar. Cualquier otro cambio de forma conserva el tamaño que ya tenía.
 */
export const shapeChangeGeometry = (table, shape, tables) => {
  if (shape !== 'RECTANGLE' || table.width !== table.height) return {};
  const rect = clampRect({ x: table.posX, y: table.posY, w: table.width + 1, h: table.height });
  if (rect.w !== table.width + 1 || hasCollision(rect, tables, table.id)) return {};
  return { width: rect.w };
};

/** Cuerpo de PUT /tables/areas/{id}/layout con solo las mesas que cambiaron; null si no hay cambios. */
export const buildLayoutPayload = (originalTables, draftTables) => {
  const originalById = new Map(originalTables.map((table) => [table.id, table]));
  const changed = draftTables.filter((table) => {
    const original = originalById.get(table.id);
    return original && geometryChanged(original, table);
  });
  if (changed.length === 0) return null;
  return {
    tables: changed.map((table) => ({
      id: table.id,
      pos_x: table.posX,
      pos_y: table.posY,
      width: table.width,
      height: table.height,
      rotation: table.rotation || 0,
    })),
  };
};

const ERROR_MESSAGES = {
  duplicate_name: 'Ya existe una con ese nombre.',
  area_has_open_tabs: 'El salón tiene mesas con cuenta abierta.',
  table_has_open_tab: 'La mesa tiene una cuenta abierta.',
  table_occupied: 'La mesa está ocupada.',
  table_not_in_area: 'La mesa no pertenece a este salón.',
  product_not_available: 'Hay un producto sin precio o no disponible en esta sucursal.',
  mixed_currencies: 'La cuenta no puede mezclar monedas distintas.',
  item_not_active: 'Algún ítem ya no está pendiente (fue cobrado, anulado o movido).',
  items_changed: 'La cuenta cambió mientras la operabas. Actualizala e intentá de nuevo.',
  tab_closed: 'La cuenta ya está cerrada.',
  tab_has_items: 'La cuenta tiene ítems pendientes: cobralos o anulalos antes de cerrarla.',
  tab_empty: 'La cuenta no tiene ítems pendientes de cobro.',
  same_table: 'Elegí una mesa distinta.',
  different_location: 'La mesa es de otra sucursal.',
  terminal_code: 'Falta seleccionar la terminal de caja.',
  payment_method: 'Falta seleccionar el medio de pago.',
  quantity: 'La cantidad no es válida.',
  guests: 'La cantidad de comensales no es válida.',
  waiter_email: 'El mozo no existe.',
  capacity: 'La capacidad debe estar entre 1 y 50.',
  shape: 'La forma no es válida.',
  name: 'El nombre es obligatorio.',
  'open cash register is required for POS sales': 'La caja está cerrada. Abrila desde Ventas para poder cobrar.',
  'terminal not found': 'La terminal de esta computadora no existe en la sucursal.',
};

/**
 * Mensaje en español para un error de axios del plugin. El backend responde "texto: código";
 * el código (metadata) es el que dice qué pasó. Sin código conocido se muestra el texto del backend con su detalle.
 */
export const describeTablesError = (error, fallback = 'No pudimos completar la operación') => {
  const raw = error?.response?.data?.error_message;
  if (typeof raw === 'string' && raw) {
    const [text, ...rest] = raw.split(':');
    const code = rest.join(':').trim();
    if (ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
    if (text.trim()) return code ? `${text.trim()}: ${code}` : text.trim();
  }
  if (error?.response?.status === 403) return 'No tenés permiso para esta operación.';
  return fallback;
};

/** Importe con su moneda; null/NaN se muestran como 0. */
export const money = (value, currency) => formatMoney(Number(value) || 0, currency || 'UYU');

/** "12 min", "1 h 05 min" desde una fecha ISO (el backend manda LocalDateTime sin zona, en hora local). */
export const elapsedLabel = (openedAt, now = Date.now()) => {
  const opened = new Date(openedAt).getTime();
  if (!Number.isFinite(opened)) return '';
  const minutes = Math.max(0, Math.floor((now - opened) / 60000));
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min`;
};

/** Ítems de una cuenta agrupados por tanda de pedido, en orden. */
export const groupByRound = (items) => {
  const rounds = new Map();
  items.forEach((item) => {
    if (!rounds.has(item.roundNo)) rounds.set(item.roundNo, []);
    rounds.get(item.roundNo).push(item);
  });
  return [...rounds.entries()].sort((a, b) => a[0] - b[0]).map(([roundNo, list]) => ({ roundNo, items: list }));
};

/** Mapea ítems de la cuenta al formato que espera el BillingModal del POS (precio congelado, sin descuentos). */
export const toBillingItems = (tabItems, currency) =>
  tabItems.map((item) => ({
    id: item.productId,
    name: item.name,
    qty: item.quantity,
    unitPrice: item.unitPrice,
    price: item.unitPrice,
    priceLocal: item.unitPrice,
    priceCurrency: currency,
    discountAmount: 0,
  }));


/**
 * Estado de caja a partir de la respuesta (o el error) de GET /cash_register/is_open: 'open', 'closed', 'wrong_terminal'
 * (400: terminal que no es de la sucursal) o 'unknown'. ('checking' y 'no_terminal' los fija la pantalla.)
 */
export const cashStateFrom = (response, error) => {
  if (!error) return response?.isOpen === true ? 'open' : 'closed';
  return error?.response?.status === 400 ? 'wrong_terminal' : 'unknown';
};

export const waiterLabel = (waiter) => `${waiter.firstName || ''} ${waiter.lastName || ''}`.trim() || waiter.email;

/**
 * Opciones del selector de mozo: el personal de la sucursal más los emails extra que hagan falta (el mozo actual de la
 * cuenta o quien opera, si no están asignados al local), sin repetir y ordenados por nombre.
 */
export const waiterOptions = (waiters, extraEmails = []) => {
  const byEmail = new Map(waiters.map((waiter) => [waiter.email, waiter]));
  extraEmails.filter(Boolean).forEach((email) => {
    if (!byEmail.has(email)) byEmail.set(email, { email, firstName: '', lastName: '' });
  });
  return [...byEmail.values()]
    .map((waiter) => ({ value: waiter.email, label: waiterLabel(waiter) }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
};
